import { useSearch } from '@tanstack/react-router';
import type { Project } from 'sdk';
import { useHotkeys } from '~/hooks/use-hot-keys';
import type { HotkeyItem } from '~/hooks/use-hot-keys-helpers';
import { useOrganizationLayoutContext } from '~/hooks/use-route-context';
import { useDropdowner } from '~/modules/common/dropdowner/use-dropdowner';
import { useSheeter } from '~/modules/common/sheeter/use-sheeter';
import { isProjectReadOnly } from '~/modules/project/use-read-only';
import { defaultPanelPrefs, useTaskBoardStore } from '~/modules/task/board/task-board-store';
import { boardSearchInputId } from '~/modules/task/board-search';
import { useTaskCardStore } from '~/modules/task/card/task-card-store';
import type { DropdownsType } from '~/modules/task/dropdowns/types';
import { cachedTasks, currentActiveTask } from '~/modules/task/helpers/active-task';
import { pickViewSections, prepareBoardPanels, renderedPanelTasks } from '~/modules/task/helpers/board-helpers';
import { toggleCreateTaskForm } from '~/modules/task/helpers/create-task';
import { setTaskCardFocus } from '~/modules/task/helpers/focus-task';
import { handleTaskDropdownClick } from '~/modules/task/helpers/task-dropdown';
import { buildFieldHandlers } from '~/modules/task/hooks/use-task-field-handlers';
import { useTaskUpdateMutation } from '~/modules/task/query';
import { useTaskInteractionStore } from '~/modules/task/task-interaction-store';
import type { ProjectResizablePanel, Task } from '~/modules/task/types';
import { useCurrentUser } from '~/modules/user/user-store';
import { isHoverContentOpen } from '~/utils/is-hover-content-open';

interface TasksHotkeysProps {
  boardId: string;
  projects: Project[];
  type: 'workspace' | 'project';
}

type StrictBoardPanel = ProjectResizablePanel;

/** Registers keyboard navigation and actions for task views. */
export function TasksHotkeys({ boardId, projects, type }: TasksHotkeysProps) {
  const { organizationId, tenantId } = useOrganizationLayoutContext();
  const user = useCurrentUser();
  const taskMutation = useTaskUpdateMutation(tenantId, organizationId);

  const q = useSearch({ strict: false, select: (search) => search.q });
  const matchMode = useSearch({ strict: false, select: (search) => search.matchMode });
  const taskSheetId = useSearch({ strict: false, select: (search) => search.taskSheetId });
  const isSheetOpen = !!taskSheetId;

  const findCurrentPanel = (allPanels: StrictBoardPanel[], taskProjectId: string, taskStatus: Task['status']) =>
    allPanels.find(({ project, sectionFilters }) => project.id === taskProjectId && (!sectionFilters || sectionFilters.status.includes(taskStatus)));

  // Resolve the focused task and its panel's rendered task list (shared by vertical nav handlers).
  const resolveVerticalNavContext = () => {
    if (!projects.length) return null;
    const boardPanelData = useTaskBoardStore.getState().panelData[boardId];
    const allPanels: StrictBoardPanel[] = prepareBoardPanels(projects, pickViewSections(boardPanelData));
    const currentTask = currentActiveTask();
    if (!currentTask) return null;

    const currentTaskCard = document.getElementById(currentTask.id);
    if (currentTaskCard?.dataset.state === 'editing') return null;

    const currentPanel = findCurrentPanel(allPanels, currentTask.projectId, currentTask.status) ?? allPanels[0];
    const prefs = boardPanelData?.[currentPanel.project.id]?.prefs || defaultPanelPrefs;
    const visibleTasks = renderedPanelTasks(currentPanel, cachedTasks(), { q, matchMode }, prefs);

    return { currentTask, visibleTasks };
  };

  // Navigate one task up/down within a panel.
  const handleVerticalArrowKeyDown = (event: KeyboardEvent) => {
    const ctx = resolveVerticalNavContext();
    if (!ctx) return;
    const { currentTask, visibleTasks } = ctx;

    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const currentTaskIndex = visibleTasks.findIndex((task) => task.id === currentTask.id);
    const nextTask = visibleTasks[currentTaskIndex + direction];
    if (nextTask) setTaskCardFocus(nextTask.id);
  };

  // Cmd + Up/Down: jump to the first/last task in the column.
  const handleColumnEdgeKeyDown = (event: KeyboardEvent) => {
    const ctx = resolveVerticalNavContext();
    if (!ctx) return;
    const { visibleTasks } = ctx;

    const targetTask = event.key === 'ArrowDown' ? visibleTasks.at(-1) : visibleTasks[0];
    if (targetTask) setTaskCardFocus(targetTask.id);
  };

  // Option + Up/Down: jump to the first task of the next/previous status group. Does nothing at the edges.
  const handleStatusGroupKeyDown = (event: KeyboardEvent) => {
    const ctx = resolveVerticalNavContext();
    if (!ctx) return;
    const { currentTask, visibleTasks } = ctx;

    // visibleTasks are sorted by status, so distinct statuses appear in column order.
    const statusesInOrder = [...new Set(visibleTasks.map((task) => task.status))];
    const currentStatusIndex = statusesInOrder.indexOf(currentTask.status);
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const targetStatus = statusesInOrder[currentStatusIndex + direction];
    if (targetStatus === undefined) return; // Already in the first/last status group.

    const targetTask = visibleTasks.find((task) => task.status === targetStatus);
    if (targetTask) setTaskCardFocus(targetTask.id);
  };

  // Find the first task whose card is (partially) visible at the top of the panel's scroll viewport.
  const getFirstViewportTaskId = (projectId: string, visibleTasks: Task[]): string | undefined => {
    const viewport = document.getElementById(`${projectId}-viewport`);
    if (!viewport) return undefined;
    const viewportTop = viewport.getBoundingClientRect().top;
    for (const task of visibleTasks) {
      const card = document.getElementById(task.id);
      if (!card) continue;
      // First card whose bottom edge clears the viewport top is the first (partially) visible task.
      if (card.getBoundingClientRect().bottom > viewportTop + 1) return task.id;
    }
    return undefined;
  };

  // Left/Right focuses the first visible task in the target panel.
  const handleHorizontalArrowKeyDown = (event: KeyboardEvent) => {
    if (!projects.length) return;
    const boardPanelData = useTaskBoardStore.getState().panelData[boardId];
    const allPanels: StrictBoardPanel[] = prepareBoardPanels(projects, pickViewSections(boardPanelData));
    const currentTask = currentActiveTask();

    const currentPanelIndex = allPanels.findIndex(
      ({ sectionFilters, project }) =>
        project.id === currentTask?.projectId && (!sectionFilters || (!!currentTask && sectionFilters.status.includes(currentTask.status))),
    );

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextPanel = allPanels[currentPanelIndex + direction];
    if (!nextPanel) return;

    const prefs = boardPanelData?.[nextPanel.project.id]?.prefs || defaultPanelPrefs;
    const visibleTasks = renderedPanelTasks(nextPanel, cachedTasks(), { q, matchMode }, prefs);
    const targetTaskId = getFirstViewportTaskId(nextPanel.project.id, visibleTasks) ?? visibleTasks[0]?.id;
    if (targetTaskId) setTaskCardFocus(targetTaskId);
  };

  const handleEscKeyPress = () => {
    // The Escape that closes a tooltip or hover card stops there: the card and its sheet keep their state (WCAG 1.4.13).
    if (isHoverContentOpen()) return;
    const task = currentActiveTask(taskSheetId);
    if (!task) return;

    if (document.activeElement instanceof HTMLElement && task.id === document.activeElement.id) {
      document.activeElement.blur();
    }

    if (isSheetOpen) return useSheeter.getState().remove(`taskSheetId-${task.id}`);

    const taskCard = document.getElementById(task.id);
    const state = taskCard?.dataset.state;
    if (state === 'collapsed') return;
    return useTaskCardStore.getState().setTaskState(task.id, state === 'editing' ? 'expanded' : 'collapsed');
  };

  const handleEnterKeyPress = () => {
    const task = currentActiveTask(taskSheetId);
    if (!task) return;

    const taskCard = document.getElementById(isSheetOpen ? `sheet-${task.id}` : task.id);
    const currentState = taskCard?.dataset.state ?? 'collapsed';
    if (isProjectReadOnly(task.projectId) && currentState === 'expanded') return;
    useTaskCardStore.getState().setTaskState(task.id, currentState === 'collapsed' ? 'expanded' : 'editing');
  };

  const handleNKeyDown = () => {
    if (!projects.length) return;
    const { draftTasks } = useTaskInteractionStore.getState();
    const openDraftProjectId = Object.keys(draftTasks)[0];

    let targetProjectId: string | undefined = openDraftProjectId;
    if (!targetProjectId) targetProjectId = currentActiveTask(taskSheetId)?.projectId;

    const targetProject = projects.find((p) => p.id === targetProjectId) ?? projects[0];
    if (isProjectReadOnly(targetProject.id)) return;
    toggleCreateTaskForm(targetProject);
  };

  // Open a field dropdown for the focused task
  const hotKeyPress = (field: DropdownsType) => {
    const targetTask = currentActiveTask(taskSheetId);
    if (!targetTask) return;

    if (isProjectReadOnly(targetTask.projectId)) return;

    const taskCard = document.getElementById(isSheetOpen ? `sheet-${targetTask.id}` : targetTask.id);
    if (!taskCard) return;
    if (document.activeElement !== taskCard) taskCard.focus();

    const triggerId = `${field}-${targetTask.id}${isSheetOpen ? '-sheet' : ''}`;
    const trigger = taskCard.querySelector(`#${triggerId}`);
    if (!(trigger instanceof HTMLButtonElement)) return useDropdowner.getState().remove();

    const handlers = buildFieldHandlers(targetTask, { taskMutation, user });
    const base = { triggerId, triggerRef: { current: trigger }, taskId: targetTask.id };

    // The switch keeps each call matched to its discriminated-union member without a cast.
    switch (field) {
      case 'labels':
        return handleTaskDropdownClick({
          ...base,
          dropdownType: 'labels',
          value: targetTask.labels,
          projectId: targetTask.projectId,
          onChange: handlers.onLabelsChange,
        });
      case 'assignedTo':
        return handleTaskDropdownClick({
          ...base,
          dropdownType: 'assignedTo',
          value: targetTask.assignedTo,
          projectId: targetTask.projectId,
          onChange: handlers.onAssignedToChange,
        });
      case 'status':
        return handleTaskDropdownClick({ ...base, dropdownType: 'status', value: targetTask.status, onChange: handlers.onStatusChange });
      case 'primaryLabel':
        return handleTaskDropdownClick({
          ...base,
          dropdownType: 'primaryLabel',
          value: targetTask.primaryLabelId,
          projectId: targetTask.projectId,
          onChange: handlers.onPrimaryLabelChange,
        });
    }
  };

  // A letter alone fires by accident under speech input (WCAG 2.1.4); Alt + A is the account sheet, so assignee is U
  const actionHotkeys: HotkeyItem[] = [
    ['Alt + U', () => hotKeyPress('assignedTo')],
    ['Alt + L', () => hotKeyPress('labels')],
    ['Alt + S', () => hotKeyPress('status')],
    ['Alt + T', () => hotKeyPress('primaryLabel')],
    ['Alt + N', handleNKeyDown],
  ];

  // Browser find cannot see virtualized cards: the first press lands in the search box, and a second press typed there is native find.
  const handleFindKeyDown = (event: KeyboardEvent) => {
    const input = document.getElementById(boardSearchInputId);
    if (!(input instanceof HTMLInputElement)) return;
    event.preventDefault();
    input.focus();
    input.select();
  };

  const stateHotkeys: HotkeyItem[] = [
    ['Escape', handleEscKeyPress],
    ['Enter', handleEnterKeyPress],
    ['meta + f', handleFindKeyDown, { preventDefault: false }],
    ['ctrl + f', handleFindKeyDown, { preventDefault: false }],
  ];

  const boardNavHotkeys: HotkeyItem[] = [
    ['ArrowRight', handleHorizontalArrowKeyDown],
    ['ArrowLeft', handleHorizontalArrowKeyDown],
  ];

  const panelNavHotkeys: HotkeyItem[] = [
    ['ArrowDown', handleVerticalArrowKeyDown],
    ['ArrowUp', handleVerticalArrowKeyDown],
    ['meta + ArrowDown', handleColumnEdgeKeyDown],
    ['meta + ArrowUp', handleColumnEdgeKeyDown],
    ['alt + ArrowDown', handleStatusGroupKeyDown],
    ['alt + ArrowUp', handleStatusGroupKeyDown],
  ];

  const gridIgnoreTags = ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', '[role="grid"]'];

  const coreHotkeys = [...stateHotkeys, ...actionHotkeys];
  useHotkeys(coreHotkeys, gridIgnoreTags);

  const navHotkeys = isSheetOpen ? [] : type === 'workspace' ? [...boardNavHotkeys, ...panelNavHotkeys] : panelNavHotkeys;
  useHotkeys(navHotkeys, gridIgnoreTags);

  return null;
}
